'use client';

import { usePathname } from 'next/navigation';
import { useCallback, useLayoutEffect, useState } from 'react';

import { getENV } from '../../utils/env';
import { useScript } from '../useScript';
import { isCheckboxRendered, recaptchaString, resetRecaptchaValues, setRecaptchaValues } from './helpers';
import type { ResetRecaptchaParams } from './useGoogleRecaptcha';

export function useGoogleRecaptchaCheckbox() {
    const recaptchaKeyCheckbox = getENV('NEXT_PUBLIC_ENTERPRISE_RECAPTCHA_KEY_CHECKBOX');

    const recaptchaSrcCheckbox = `https://www.google.com/recaptcha/enterprise.js?render=explicit`;
    const scriptSrc = getENV('NEXT_PUBLIC_ENTERPRISE_RECAPTCHA_ENABLED') ? recaptchaSrcCheckbox : '';
    const recaptchaStatusCheckbox = useScript(scriptSrc);
    const [recaptchaIdCheckbox, setRecaptchaIdCheckbox] = useState<number>();

    const [useRecaptchaCheckbox, setUseRecaptchaCheckbox] = useState<string | null>(null);
    const [isRecaptchaCompleteCheckbox, setIsRecaptchaCompleteCheckbox] = useState<boolean>(false);
    const [recaptchaErrorCheckbox, setRecaptchaErrorCheckbox] = useState<string | null>(null);

    const pathname = usePathname();
    useLayoutEffect(() => {
        // effect removes recaptcha checkbox on url change.  Attempts before and after changing urls
        setUseRecaptchaCheckbox(null);

        return () => {
            setUseRecaptchaCheckbox(null);
        };
    }, [pathname]);

    const resetRecaptchaCheckbox = useCallback(
        async (reason?: ResetRecaptchaParams) => {
            if (reason === 'failed') {
                setRecaptchaErrorCheckbox(recaptchaString.checkboxFail);
            }

            await resetRecaptchaValues(recaptchaIdCheckbox);
            setIsRecaptchaCompleteCheckbox(false);
        },
        [recaptchaIdCheckbox]
    );

    /**
     * render recaptcha checkbox
     */
    const renderRecaptchaCheckbox = useCallback(
        async (id: string) => {
            if (recaptchaStatusCheckbox === 'ready' && !isCheckboxRendered(id)) {
                await window.grecaptcha?.enterprise.ready(async () => {
                    const recaptchaId = window.grecaptcha?.enterprise.render(id, {
                        sitekey: recaptchaKeyCheckbox,
                        // on success
                        callback: async (recaptchaToken: string) => {
                            await setRecaptchaValues(recaptchaToken, true);
                            setIsRecaptchaCompleteCheckbox(true);
                            setRecaptchaErrorCheckbox(null);
                        },
                        // on expired
                        'expired-callback': () => {
                            resetRecaptchaCheckbox('expired');
                        },
                        // on failure
                        'error-callback': () => {
                            resetRecaptchaCheckbox('failed');
                        },
                    });

                    setRecaptchaErrorCheckbox(recaptchaString.checkboxRender);
                    setRecaptchaIdCheckbox(recaptchaId);
                });
            }
        },
        [recaptchaStatusCheckbox, recaptchaKeyCheckbox, resetRecaptchaCheckbox]
    );

    return {
        recaptchaSrcCheckbox: scriptSrc,
        recaptchaStatusCheckbox,
        recaptchaIdCheckbox,
        useRecaptchaCheckbox,
        setUseRecaptchaCheckbox,
        renderRecaptchaCheckbox,
        isRecaptchaCompleteCheckbox,
        resetRecaptchaCheckbox,
        setRecaptchaIdCheckbox,
        recaptchaErrorCheckbox,
        setRecaptchaErrorCheckbox,
        siteKeyCheckbox: recaptchaKeyCheckbox,
    };
}
